import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Languages, MoveRight, Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#stay", label: t.nav.stay },
    { href: "#experiences", label: t.nav.experiences },
    { href: "#nearby", label: t.nav.nearby },
    { href: "#cricket", label: t.nav.cricket },
    { href: "#dining", label: t.nav.dining },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-cream-100/85 border-b border-forest-100" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo size={42} light={!scrolled} testid="nav-logo-link" />

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.href.replace('#','')}`}
               className={`text-[13px] tracking-wide transition-colors ${scrolled ? 'text-forest-700 hover:text-gold-600' : 'text-cream-100 hover:text-gold-400'}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle-btn"
            className={`hidden sm:inline-flex items-center gap-2 px-3 py-1.5 border rounded-full text-[10px] uppercase tracking-widest2 transition-colors
              ${scrolled ? 'border-forest-200 text-forest-700 hover:bg-cream-200' : 'border-cream-100/40 text-cream-100 hover:bg-cream-100/10'}`}
          >
            <Languages className="w-3.5 h-3.5" />
            {lang === "en" ? "বাংলা" : "English"}
          </button>
          <a href="#booking" data-testid="nav-book-btn"
             className="hidden md:inline-flex items-center gap-2 bg-forest-700 text-cream-100 px-5 py-2.5 rounded-full text-[12px] tracking-wide hover:bg-forest-800 transition-all hover:gap-3 border border-gold-600/40">
            {t.nav.book} <MoveRight className="w-4 h-4" />
          </a>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn"
                  className={`lg:hidden p-2 ${scrolled ? 'text-forest-700' : 'text-cream-100'}`}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream-100 border-t border-forest-100" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-4">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                 className="block text-base text-forest-700" data-testid={`mobile-nav-${l.href.replace('#','')}`}>
                {l.label}
              </a>
            ))}
            <button onClick={toggle} data-testid="mobile-lang-toggle"
                    className="text-sm text-gold-600 underline">
              {lang === "en" ? "বাংলায় দেখুন" : "View in English"}
            </button>
            <a href="#booking" onClick={() => setOpen(false)} data-testid="mobile-book-btn"
               className="block w-full text-center bg-forest-700 text-cream-100 py-3 rounded-full text-sm">
              {t.nav.book}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
